import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { motion } from "framer-motion";
import * as THREE from "three";
import { feature } from "topojson-client";
import { geoContains } from "d3-geo";
import land110m from "@/data/land-110m.json";
import { ALIGNMENTS } from "@/data/enhancements";

const R = 2;
const CAM_Z = 7;
const FOV = 42;
const HALF_H = Math.tan((FOV / 2) * (Math.PI / 180)) * CAM_Z;

function latLngToVec3(lat, lng, r = R) {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lng + 180) * Math.PI) / 180;
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const Globe3D = forwardRef(function Globe3D(
  { sites, selectedId, tourId, routeIds, visibleIds, origins, onSelect, onTelemetry },
  ref
) {
  const containerRef = useRef(null);
  const stateRef = useRef(null);
  const telemetryCb = useRef(null);
  const selectCb = useRef(null);
  const [hover, setHover] = useState(null);
  const hoverRef = useRef(null);

  telemetryCb.current = onTelemetry;
  selectCb.current = onSelect;

  useImperativeHandle(ref, () => ({
    flyTo: (site) => {
      const s = stateRef.current;
      if (!s || !s.ready) return;
      const target = -Math.PI / 2 - (site.lng * Math.PI) / 180;
      while (target - s.targetYaw > Math.PI) s.targetYaw += Math.PI * 2;
      while (target - s.targetYaw < -Math.PI) s.targetYaw -= Math.PI * 2;
      s.targetYaw = target;
      s.targetPitch = clamp((site.lat * Math.PI) / 180, -1.1, 1.1);
      s.flying = true;
    },
  }));

  useEffect(() => {
    const s = stateRef.current;
    if (s) {
      const focus = tourId || selectedId;
      s.selectedId = focus;
      s.drawerOpen = !!selectedId;
      s.tourMode = !!tourId;
      s.alignId = ALIGNMENTS[focus] ? focus : null;
      s.alignTarget = focus ? 1 : 0;
      s.alignOpacity = 0;
    }
  }, [selectedId, tourId]);

  useEffect(() => {
    stateRef.current?.rebuildRoute?.(routeIds || []);
  }, [routeIds]);

  useEffect(() => {
    stateRef.current?.rebuildOrigins?.(origins || []);
  }, [origins]);

  useEffect(() => {
    const s = stateRef.current;
    if (s) s.visibleIds = visibleIds || null;
  }, [visibleIds]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isCoarse = window.matchMedia("(pointer: coarse)").matches;

    /* ---------- device tier: scale geometry + resolution for weaker hardware ---------- */
    const cores = navigator.hardwareConcurrency || 8;
    const mem = navigator.deviceMemory || 8;
    const weak = cores <= 4 || mem <= 4 || window.innerWidth < 768;
    const tier = isCoarse && weak ? "low" : isCoarse || weak ? "mid" : "high";
    const Q = {
      high: { dpr: 2, latStep: 1.6, sphereSeg: 64, atmoSeg: 48, stars: [1300, 180], aa: true },
      mid: { dpr: 1.5, latStep: 2.0, sphereSeg: 48, atmoSeg: 36, stars: [800, 120], aa: true },
      low: { dpr: 1.25, latStep: 2.6, sphereSeg: 40, atmoSeg: 28, stars: [450, 70], aa: false },
    }[tier];
    container.dataset.tier = tier;

    /* ---------- renderer / scene ---------- */
    const renderer = new THREE.WebGLRenderer({
      antialias: Q.aa,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, Q.dpr));
    renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(renderer.domElement);
    renderer.domElement.style.touchAction = isCoarse ? "pan-y" : "none";
    renderer.domElement.style.display = "block";

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      FOV,
      window.innerWidth / window.innerHeight,
      0.1,
      200
    );
    camera.position.set(0, 0, CAM_Z);

    const offsetGroup = new THREE.Group();
    scene.add(offsetGroup);
    const pitchGroup = new THREE.Group();
    offsetGroup.add(pitchGroup);
    const yawGroup = new THREE.Group();
    pitchGroup.add(yawGroup);

    /* ---------- base sphere with warm rim ---------- */
    const baseMat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0x0e1018) },
        uGlow: { value: new THREE.Color(0xd4af37) },
      },
      vertexShader: `
        varying vec3 vN; varying vec3 vP;
        void main(){
          vN = normalize(normalMatrix * normal);
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          vP = mv.xyz;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        uniform vec3 uColor; uniform vec3 uGlow;
        varying vec3 vN; varying vec3 vP;
        void main(){
          vec3 vd = normalize(-vP);
          float rim = pow(1.0 - abs(dot(normalize(vN), vd)), 2.6);
          gl_FragColor = vec4(mix(uColor, uGlow, rim * 0.5), 1.0);
        }`,
    });
    yawGroup.add(new THREE.Mesh(new THREE.SphereGeometry(R - 0.03, Q.sphereSeg, Q.sphereSeg), baseMat));

    /* ---------- atmosphere ---------- */
    const atmoMat = new THREE.ShaderMaterial({
      vertexShader: `
        varying vec3 vN;
        void main(){
          vN = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: `
        varying vec3 vN;
        void main(){
          float intensity = pow(max(0.6 - dot(vN, vec3(0.0, 0.0, 1.0)), 0.0), 4.0) * 0.5;
          gl_FragColor = vec4(0.83, 0.69, 0.22, 1.0) * intensity;
        }`,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
    });
    offsetGroup.add(new THREE.Mesh(new THREE.SphereGeometry(R * 1.19, Q.atmoSeg, Q.atmoSeg), atmoMat));

    /* ---------- land dots from embedded topojson ---------- */
    const land = feature(land110m, land110m.objects.countries);
    const dotPositions = [];
    for (let lat = -84; lat <= 84; lat += Q.latStep) {
      const rad = Math.cos((lat * Math.PI) / 180);
      const step = Math.min(6, Q.latStep / Math.max(rad, 0.22));
      for (let lng = -180; lng < 180; lng += step) {
        if (geoContains(land, [lng, lat])) {
          const v = latLngToVec3(lat, lng, R + 0.004);
          dotPositions.push(v.x, v.y, v.z);
        }
      }
    }
    const dotGeo = new THREE.BufferGeometry();
    container.dataset.dots = String(dotPositions.length / 3);
    dotGeo.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(dotPositions, 3)
    );
    yawGroup.add(
      new THREE.Points(
        dotGeo,
        new THREE.PointsMaterial({
          color: 0xd4af37,
          size: 0.023,
          sizeAttenuation: true,
          transparent: true,
          opacity: 0.82,
          depthWrite: false,
        })
      )
    );

    /* ---------- graticule ---------- */
    const gridPts = [];
    const gR = R - 0.005;
    for (let lat = -60; lat <= 60; lat += 30) {
      for (let lng = -180; lng < 180; lng += 3) {
        const a = latLngToVec3(lat, lng, gR);
        const b = latLngToVec3(lat, lng + 3, gR);
        gridPts.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
    }
    for (let lng = -180; lng < 180; lng += 30) {
      for (let lat = -87; lat < 87; lat += 3) {
        const a = latLngToVec3(lat, lng, gR);
        const b = latLngToVec3(lat + 3, lng, gR);
        gridPts.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
    }
    const gridGeo = new THREE.BufferGeometry();
    gridGeo.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(gridPts, 3)
    );
    yawGroup.add(
      new THREE.LineSegments(
        gridGeo,
        new THREE.LineBasicMaterial({
          color: 0xd4af37,
          transparent: true,
          opacity: 0.07,
          depthWrite: false,
        })
      )
    );

    /* ---------- starfield ---------- */
    const makeStars = (count, color, size, spread) => {
      const arr = [];
      for (let i = 0; i < count; i++) {
        const v = new THREE.Vector3(
          Math.random() - 0.5,
          Math.random() - 0.5,
          Math.random() - 0.5
        )
          .normalize()
          .multiplyScalar(spread + Math.random() * 25);
        arr.push(v.x, v.y, v.z);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.Float32BufferAttribute(arr, 3));
      return new THREE.Points(
        g,
        new THREE.PointsMaterial({
          color,
          size,
          sizeAttenuation: true,
          transparent: true,
          opacity: 0.75,
          depthWrite: false,
        })
      );
    };
    scene.add(makeStars(Q.stars[0], 0xf8fafc, 0.05, 35));
    scene.add(makeStars(Q.stars[1], 0xff9f1c, 0.07, 35));

    /* ---------- orbit ring + satellite ---------- */
    const ringPts = new THREE.EllipseCurve(0, 0, R * 1.62, R * 1.62)
      .getPoints(180)
      .map((p) => new THREE.Vector3(p.x, 0, p.y));
    const orbit = new THREE.LineLoop(
      new THREE.BufferGeometry().setFromPoints(ringPts),
      new THREE.LineBasicMaterial({
        color: 0xd4af37,
        transparent: true,
        opacity: 0.2,
      })
    );
    orbit.rotation.set(1.15, 0, -0.35);
    offsetGroup.add(orbit);
    const satellite = new THREE.Mesh(
      new THREE.SphereGeometry(0.035, 12, 12),
      new THREE.MeshBasicMaterial({ color: 0xff9f1c })
    );
    offsetGroup.add(satellite);

    /* ---------- site markers ---------- */
    const markers = [];
    sites.forEach((site, i) => {
      const pos = latLngToVec3(site.lat, site.lng, R + 0.012);
      const g = new THREE.Group();
      g.position.copy(pos);
      g.lookAt(pos.clone().multiplyScalar(2));
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.017, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0xff9f1c, transparent: true, opacity: 1 })
      );
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.03, 0.037, 32),
        new THREE.MeshBasicMaterial({
          color: 0xff9f1c,
          transparent: true,
          opacity: 0.8,
          side: THREE.DoubleSide,
          depthWrite: false,
        })
      );
      const hit = new THREE.Mesh(
        new THREE.SphereGeometry(0.08, 8, 8),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
      );
      hit.userData.siteIndex = i;
      g.add(dot, ring, hit);
      yawGroup.add(g);
      markers.push({ site, dot, ring, hit, base: pos.clone(), curF: 1 });
    });

    /* ---------- sky alignment constellations ---------- */
    const alignGroups = {};
    Object.entries(ALIGNMENTS).forEach(([siteId, a]) => {
      const site = sites.find((x) => x.id === siteId);
      if (!site) return;
      const pos = latLngToVec3(site.lat, site.lng, 3.55);
      const g = new THREE.Group();
      g.position.copy(pos);
      g.lookAt(pos.clone().multiplyScalar(2));
      g.visible = false;
      const starPts = a.stars.map(([u, v]) => new THREE.Vector3(u, v, 0));
      const stars = new THREE.Points(
        new THREE.BufferGeometry().setFromPoints(starPts),
        new THREE.PointsMaterial({
          color: 0xe8f4ff,
          size: 0.075,
          transparent: true,
          opacity: 0,
          depthWrite: false,
        })
      );
      const linePts = [];
      a.lines.forEach(([i, j]) => {
        linePts.push(starPts[i].clone(), starPts[j].clone());
      });
      const lines = new THREE.LineSegments(
        new THREE.BufferGeometry().setFromPoints(linePts),
        new THREE.LineBasicMaterial({
          color: 0x38bdf8,
          transparent: true,
          opacity: 0,
          depthWrite: false,
        })
      );
      const surface = latLngToVec3(site.lat, site.lng, R + 0.02);
      const beam = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([surface, pos.clone()]),
        new THREE.LineBasicMaterial({
          color: 0x38bdf8,
          transparent: true,
          opacity: 0,
          depthWrite: false,
        })
      );
      g.add(stars, lines);
      yawGroup.add(g, beam);
      alignGroups[siteId] = { g, stars, lines, beam };
    });

    /* ---------- expedition route arcs ---------- */
    const routeLine = new THREE.Line(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({
        color: 0xd4af37,
        transparent: true,
        opacity: 0.65,
        depthWrite: false,
      })
    );
    routeLine.visible = false;
    yawGroup.add(routeLine);
    const rebuildRoute = (ids) => {
      const pts = ids.map((id) => sites.find((x) => x.id === id)).filter(Boolean);
      if (pts.length < 2) {
        routeLine.visible = false;
        return;
      }
      const arc = [];
      for (let k = 0; k < pts.length - 1; k++) {
        const a = latLngToVec3(pts[k].lat, pts[k].lng, 1).normalize();
        const b = latLngToVec3(pts[k + 1].lat, pts[k + 1].lng, 1).normalize();
        const ang = a.angleTo(b);
        const segs = Math.max(12, Math.ceil(ang * 40));
        for (let j = 0; j <= segs; j++) {
          const t = j / segs;
          const lift = R + 0.03 + Math.sin(Math.PI * t) * ang * 0.22;
          const p = new THREE.Vector3()
            .copy(a)
            .multiplyScalar(Math.sin((1 - t) * ang))
            .addScaledVector(b, Math.sin(t * ang))
            .divideScalar(Math.sin(ang) || 1)
            .normalize()
            .multiplyScalar(lift);
          arc.push(p);
        }
      }
      routeLine.geometry.dispose();
      routeLine.geometry = new THREE.BufferGeometry().setFromPoints(arc);
      routeLine.visible = true;
    };
    rebuildRoute(routeIds || []);

    /* ---------- visitor origins: faint cyan points where today's votes come from ---------- */
    const originGroup = new THREE.Group();
    yawGroup.add(originGroup);
    const originMat = new THREE.MeshBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.55, depthWrite: false });
    const originRingMat = new THREE.MeshBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.25, side: THREE.DoubleSide, depthWrite: false });
    const rebuildOrigins = (list) => {
      originGroup.clear();
      list.forEach((o) => {
        const pos = latLngToVec3(o.lat, o.lng, R + 0.012);
        const size = 0.018 + Math.min(0.03, Math.log1p(o.n) * 0.012);
        const dot = new THREE.Mesh(new THREE.SphereGeometry(size, 10, 10), originMat);
        dot.position.copy(pos);
        const ring = new THREE.Mesh(new THREE.RingGeometry(size * 1.8, size * 2.2, 24), originRingMat);
        ring.position.copy(pos);
        ring.lookAt(pos.clone().multiplyScalar(2));
        ring.userData.phase = Math.random() * Math.PI * 2;
        originGroup.add(dot, ring);
      });
    };
    rebuildOrigins(origins || []);

    /* ---------- interaction state ---------- */
    const s = {
      ready: true,
      rebuildRoute,
      rebuildOrigins,
      originGroup,
      tourMode: false,
      yaw: -Math.PI / 2 + 2.4,
      targetYaw: -Math.PI / 2,
      pitch: 0,
      targetPitch: 0.35,
      flying: false,
      dragging: false,
      downXY: null,
      prevXY: null,
      velYaw: 0,
      mx: 0,
      my: 0,
      selectedId: null,
      drawerOpen: false,
      telemAcc: 1,
    };
    stateRef.current = s;

    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const wp = new THREE.Vector3();

    const pick = (cx, cy) => {
      ndc.set((cx / window.innerWidth) * 2 - 1, -(cy / window.innerHeight) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(
        markers.map((m) => m.hit),
        false
      );
      if (!hits.length) return null;
      const m = markers[hits[0].object.userData.siteIndex];
      m.hit.getWorldPosition(wp);
      const facing = wp
        .clone()
        .sub(offsetGroup.position)
        .normalize()
        .dot(camera.position.clone().sub(offsetGroup.position).normalize());
      return facing > 0.2 ? m : null;
    };

    const onDown = (e) => {
      s.downXY = [e.clientX, e.clientY];
      s.prevXY = [e.clientX, e.clientY];
      s.velYaw = 0;
      container.style.cursor = "grabbing";
    };

    const onMove = (e) => {
      s.mx = (e.clientX / window.innerWidth) * 2 - 1;
      s.my = (e.clientY / window.innerHeight) * 2 - 1;
      if (s.downXY) {
        const dx = e.clientX - s.prevXY[0];
        const dy = e.clientY - s.prevXY[1];
        if (
          Math.abs(e.clientX - s.downXY[0]) + Math.abs(e.clientY - s.downXY[1]) >
          5
        ) {
          s.dragging = true;
          s.flying = false;
        }
        s.targetYaw += dx * 0.0045;
        s.targetPitch = clamp(s.targetPitch + dy * 0.0035, -1.15, 1.15);
        s.velYaw = dx * 0.0045;
        s.prevXY = [e.clientX, e.clientY];
      } else {
        const m = pick(e.clientX, e.clientY);
        const site = m ? m.site : null;
        if ((site && !hoverRef.current) || (!site && hoverRef.current) || (site && site.id !== hoverRef.current?.id)) {
          hoverRef.current = site;
          setHover(site ? { site, x: e.clientX, y: e.clientY } : null);
        } else if (site) {
          setHover({ site, x: e.clientX, y: e.clientY });
        }
        container.style.cursor = site ? "pointer" : "grab";
      }
    };

    const onUp = (e) => {
      container.style.cursor = "grab";
      if (s.downXY && !s.dragging) {
        const m = pick(e.clientX, e.clientY);
        if (m) {
          const target = -Math.PI / 2 - (m.site.lng * Math.PI) / 180;
          while (target - s.targetYaw > Math.PI) s.targetYaw += Math.PI * 2;
          while (target - s.targetYaw < -Math.PI) s.targetYaw -= Math.PI * 2;
          s.targetYaw = target;
          s.targetPitch = clamp((m.site.lat * Math.PI) / 180, -1.1, 1.1);
          s.flying = true;
          selectCb.current?.(m.site);
        }
      } else if (Math.abs(s.velYaw) > 0.0004) {
        s.targetYaw += s.velYaw * 14;
      }
      s.downXY = null;
      s.dragging = false;
    };

    const onLeave = () => {
      s.downXY = null;
      s.dragging = false;
      if (hoverRef.current) {
        hoverRef.current = null;
        setHover(null);
      }
    };

    container.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    container.addEventListener("pointerleave", onLeave);

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", onResize);

    /* pause the render loop while the tab is hidden */
    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
      } else {
        clock.getDelta();
        raf = requestAnimationFrame(animate);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    /* ---------- render loop ---------- */
    const clock = new THREE.Clock();
    let raf;

    function animate() {
      raf = requestAnimationFrame(animate);
      const dt = Math.min(clock.getDelta(), 0.05);
      const t = clock.getElapsedTime();

      /* breathe the visitor-origin rings */
      for (const child of originGroup.children) {
        if (child.userData.phase !== undefined) {
          const k = 1 + 0.35 * Math.sin(t * 1.6 + child.userData.phase);
          child.scale.setScalar(k);
          child.material.opacity = 0.12 + 0.18 * (0.5 + 0.5 * Math.sin(t * 1.6 + child.userData.phase));
        }
      }

      if (s.flying) {
        s.yaw += (s.targetYaw - s.yaw) * 0.075;
        s.pitch += (s.targetPitch - s.pitch) * 0.075;
        if (
          Math.abs(s.targetYaw - s.yaw) < 0.003 &&
          Math.abs(s.targetPitch - s.pitch) < 0.003
        ) {
          s.flying = false;
        }
      } else if (s.dragging) {
        s.yaw = s.targetYaw;
        s.pitch = s.targetPitch;
      } else {
        if (Math.abs(s.velYaw) > 0.0004) {
          s.targetYaw += s.velYaw;
          s.velYaw *= 0.93;
        }
        if (!hoverRef.current && !s.tourMode) s.targetYaw += 0.05 * dt;
        s.yaw += (s.targetYaw - s.yaw) * 0.1;
        s.pitch += (s.targetPitch - s.pitch) * 0.1;
      }

      yawGroup.rotation.y = s.yaw;
      pitchGroup.rotation.x = s.pitch;

      /* framing: keep the globe clear of the hero text column on desktop,
         center on mobile, dodge the drawer / tour card */
      const desktop = window.innerWidth >= 1024;
      const aspect = window.innerWidth / window.innerHeight;
      const halfW = HALF_H * aspect;
      let wantX;
      let wantY;
      let wantScale;
      if (s.tourMode) {
        wantX = 0;
        wantY = desktop ? 0.75 : 1.05;
        wantScale = desktop ? 0.9 : 0.68;
      } else if (desktop) {
        wantScale = clamp(aspect / 2.1, 0.72, 1);
        const textFrac = Math.min(0.62, 860 / window.innerWidth);
        const minX = -halfW + textFrac * 2 * halfW + 2.45 * wantScale;
        wantX = s.drawerOpen ? -1.5 : clamp(Math.max(1.55, minX), 0, halfW - 0.9);
        wantY = 0;
      } else {
        wantX = 0;
        wantY = s.drawerOpen ? 0.95 : 0;
        wantScale = 0.78;
      }
      offsetGroup.position.x += (wantX - offsetGroup.position.x) * 0.06;
      offsetGroup.position.y += (wantY - offsetGroup.position.y) * 0.06;
      const sc = offsetGroup.scale.x + (wantScale - offsetGroup.scale.x) * 0.06;
      offsetGroup.scale.setScalar(sc);

      /* camera parallax — look at a fixed origin so the offset actually shifts the globe on screen */
      camera.position.x += (s.mx * 0.35 - camera.position.x) * 0.04;
      camera.position.y += (-s.my * 0.25 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      /* sky alignment fade */
      s.alignOpacity += (s.alignTarget - s.alignOpacity) * 0.04;
      Object.entries(alignGroups).forEach(([id, A]) => {
        const t = id === s.alignId ? s.alignOpacity : 0;
        A.stars.material.opacity = t * 0.95;
        A.lines.material.opacity = t * 0.55;
        A.beam.material.opacity = t * 0.3;
        const vis = t > 0.02;
        A.g.visible = vis;
        A.beam.visible = vis;
      });

      /* markers */
      markers.forEach((m, i) => {
        const selected = s.selectedId === m.site.id;
        const hovered = hoverRef.current?.id === m.site.id;
        const dimF =
          s.visibleIds && !s.visibleIds.has(m.site.id) ? 0.12 : 1;
        m.curF += (dimF - m.curF) * 0.1;
        const pulse = 1 + Math.sin(t * 2.2 + i * 1.3) * 0.3;
        const fade = 0.5 + 0.5 * m.curF;
        m.ring.scale.setScalar(
          (selected ? 2.1 + Math.sin(t * 3) * 0.35 : pulse * (hovered ? 1.6 : 1)) * fade
        );
        m.dot.scale.setScalar((selected ? 2 : hovered ? 1.7 : 1) * fade);
        m.dot.material.color.setHex(selected ? 0xffd700 : hovered ? 0xffd700 : 0xff9f1c);
        m.dot.material.opacity = m.curF;
        m.ring.material.opacity = (selected ? 1 : hovered ? 0.95 : 0.7) * m.curF;
        m.hit.visible = m.curF > 0.3;
      });

      /* satellite on the ring */
      const a = t * 0.22;
      const local = new THREE.Vector3(Math.cos(a) * R * 1.62, 0, Math.sin(a) * R * 1.62);
      satellite.position.copy(local.applyEuler(orbit.rotation));

      /* telemetry — front-center coordinates, throttled */
      s.telemAcc += dt;
      if (s.telemAcc > 0.18 && telemetryCb.current) {
        s.telemAcc = 0;
        let lng = -90 - (s.yaw * 180) / Math.PI;
        lng = (((lng + 180) % 360) + 360) % 360 - 180;
        const lat = clamp((s.pitch * 180) / Math.PI, -90, 90);
        telemetryCb.current(lat, lng);
      }

      renderer.render(scene, camera);
    }
    animate();

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
      container.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      container.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-0" data-testid="globe-canvas" aria-label="Interactive 3D globe">
      <div ref={containerRef} className="absolute inset-0" style={{ cursor: "grab" }} />
      {hover && (
        <div
          className="pointer-events-none absolute z-30"
          style={{ left: hover.x + 18, top: hover.y - 14 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className="border border-[#D4AF37]/40 bg-[#12141C]/95 px-3.5 py-2.5 backdrop-blur"
          >
            <div className="font-mono text-[11px] tracking-[0.2em] text-[#FF9F1C]">
              {hover.site.name.toUpperCase()}
            </div>
            <div className="mt-1 font-mono text-[10px] text-white/50">
              {hover.site.lat.toFixed(2)}° · {hover.site.lng.toFixed(2)}°
            </div>
            <div className="mt-1.5 font-mono text-[8px] tracking-[0.3em] text-white/35">
              CLICK TO EXPLORE
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
});

export default Globe3D;
